'use client'

import { Button, Container, Icon, Typography, TextField, NavItem, Pagination, Drawer, Badge, Divider, Link, NavBar } from '@shc/ui'
import { type ChangeEvent, type KeyboardEvent, type ComponentPropsWithoutRef, useState } from 'react'
import { useInstantSearch, usePagination, useClearRefinements, useSearchBox, useCurrentRefinements } from 'react-instantsearch'
import SearchFacetMenu from '@components/search-facet-menu'
import { doScroll } from '@hooks/useCustomScroll'
import { debounceQueryHook } from '@lib/search'
import Image from 'next/image'
import SearchResultItem from '@components/search-result-item'
import ProviderSearchResult from '@components/search-result-doctor'
import FilteredPromo from '@components/filtered-promos'
import News2knowSearchSkeleton from '@components/news2know-search-skeleton'
import { usePathname } from 'next/navigation'
import { n2kFacetConfigMap, searchInputDebounceMs } from '@lib/constants'
import useAnalytics from '@hooks/use-analytics'

const queryHook = debounceQueryHook(searchInputDebounceMs);

type SearchNavProps = {
  features: string[];
}

export function SearchNav({ features }: SearchNavProps): JSX.Element {
  const pathname = usePathname()
  const { track } = useAnalytics()
  const searchRoutes = [];
  if( features.includes('doctors') ){
    searchRoutes.push({ name: 'Doctors', path: '/providers/search/doctors' });
  }
  searchRoutes.push({ name: 'Related pages', path: '/providers/search/related-pages' });
  searchRoutes.push({ name: 'News2Know', path: '/providers/search/news2know' });
  if( features.includes('doctors') ){
    searchRoutes.push({ name: 'Meetings and events', path: '/providers/search/events' });
  }

  const onClick = (e: React.MouseEvent<HTMLAnchorElement>, title: string) => {
    track({
      event: {
        name: 'navigation_click',
        data: {
          navigation_tree: title,
          navigation_level: 1,
          navigation_subject: title,
          navigation_url: e.currentTarget.href,
        },
      },
      contexts: [{ name: 'section', data: { section_name: 'subnav' } }],
    })
  }

  return (
    <NavBar className='bg-[white] [box-shadow:0px_2px_2px_0px_rgba(0,_0,_0,_0.05)]'>
      {searchRoutes.map((item) => (
        <NavItem key={item.path} href={item.path} active={pathname === item.path} onClick={(e) => onClick(e, item.name)}>
          {item.name}
        </NavItem>
      ))}
    </NavBar>
  )
}

export interface SearchHeroProps extends ComponentPropsWithoutRef<'div'> {
  title: string
  restrictedRegions: string[]
  features: string[]
  promos: any
}

interface SearchResultsProps {
  nbHits: number;
  results: any;
  queryText: string;
  pathname: string;
  promos: any
}

const SearchResults = ({ nbHits, results, promos }: SearchResultsProps) => {
  const aroundLatLng = results?.aroundLatLng ?? results?._state?.aroundLatLng

  return (
      <>
        <div className='flex flex-col gap-5 lg:gap-8 xl:mt-0'>
          <p><span className='font-bold'>{nbHits}</span> {nbHits > 1 ? 'results' : 'result' }</p>
        </div>
        { results?.userData && promos && (
          <FilteredPromo userData={results.userData} promos={promos} />
        )}
        <Divider className='mt-8' />
        {results?.hits.map((item: any) => {
          // Detect hits that represent providers (physicians or provider pages).
          // We render these with `ProviderSearchResult` because they require
          // provider-specific layout and logic (addresses, places, contact info).
          // `aroundLatLng` from Algolia results is forwarded so provider cards
          // can compute/display nearest practice locations or distance-based
          // UI when available.
          const isProviderHit = Boolean(item.physicianid || item.providerid || item.contentType === 'provider')
          if (isProviderHit) {
            return (
              <div key={item.slug ?? item.objectID}>
                <ProviderSearchResult hit={item} aroundLatLng={aroundLatLng} />
              </div>
            )
          }

          return (
            <div key={item.slug ?? item.objectID}> {/* this useless div is here bc console was complaining about unique keys */}
              <SearchResultItem item={item}
                component_text={item.title || item.headline || 'News2Know'}
                component_url={item.contentType === 'news2knowArticle' ? `/providers/news2know/${item.slug}` : item.url}
              />
            </div>
          )
        })}
      </>
  )
}

const Search = ({
  title,
  restrictedRegions,
  features,
  promos,
  ...props
}: SearchHeroProps) => {

  const pathname = usePathname();

  const { results, status: instantSearchStatus } = useInstantSearch()
  const { nbHits, currentRefinement, nbPages, refine: refinePagination } = usePagination()
  const currentRefinements = useCurrentRefinements()

  //state
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState<boolean>(false)

  // derived variables
  const facets = results.renderingContent?.facetOrdering?.facets?.order ?? []
  const showErrorState = instantSearchStatus === 'error'
  const showLoadState = instantSearchStatus === 'loading'
  const showNoResultsState = nbHits === 0 && !showLoadState && !showErrorState
  const showResults = !showErrorState && !showNoResultsState && !showLoadState
  const showPagination = showResults && nbPages > 1
  const showRefinements = currentRefinements.items?.length > 0
  
  const { refine } = useClearRefinements()
  
  const searchBox = useSearchBox({queryHook})
  const [queryValue, setQueryValue] = useState(searchBox.query)

  // Handlers
  const onPageChange = (page: number) => {
    refinePagination(page - 1)
    doScroll(0)
  }

  /* Handlers */

  const handleQueryKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if( event.key === 'Enter' ){
      event.preventDefault()
      return false
    }
    return true
  }
  const handleQueryValueChange = (event: ChangeEvent<HTMLInputElement>) => {
    const q = event.target.value
    setQueryValue(q)
    searchBox.refine(q)
    if (q === '') {
      refine()
    }
  }

  const isRelatedPages = pathname === '/providers/search/related-pages'

  return (
    <div {...props} className='providers-search'>
      <Drawer
        open={isFilterMenuOpen}
        onClose={() => setIsFilterMenuOpen(false)}
        aria-label="Search filters">
        <div
          className="flex flex-row items-start justify-between border-b pt-1 xl:pt-0 mb-5"
          data-testid="menu-drawer-header">
          <Typography variant="h5" className="w-full pb-5 text-left">
            Filters
          </Typography>
        </div>
        <SearchFacetMenu facets={facets} facetConfigMap={n2kFacetConfigMap} restrictedRegions={restrictedRegions}/>
      </Drawer>
      <form
        autoComplete="off"
        className="bg-primary "
        >
        <Container className="grid grid-cols-12 col-span-12 col-start-1 xl:col-span-8 xl:col-start-1 py-6 md:py-8 lg:py-15 gap-4 md:gap-8">
          <div className="col-span-12 col-start-1 xl:col-span-8 xl:col-start-1">
            <Typography variant="h1" className="w-full text-white">
              {title}
            </Typography>
          </div>
          {/* search form fields */}
          <div className="col-start-1 col-span-12 md:col-span-6 xl:col-start-1 xl:col-span-6">
            <TextField
              type="search"
              value={queryValue}
              onChange={handleQueryValueChange}
              onKeyDown={handleQueryKeyDown}
              startDecorator={<Icon className="h-3.5 " icon="magnifying-glass" />}
              placeholder='What can we help you find?'
              className='input-reset n2k-search'
            />
          </div>
        </Container>
      </form>

      {/* NAV BAR */}
      <SearchNav features={features} />

      <Container className={!isRelatedPages ? 'grid grid-cols-12 col-span-12 gap-6 text-gray-700 py-8 xl:py-15' : 'container grid grid-cols-12 py-8 xl:py-15'}>
        <div className={!isRelatedPages ? 'col-span-12' : 'col-span-12 xl:col-span-9 space-y-15 lg:space-y-20'}>
          <div className={!isRelatedPages ? 'flex flex-rows gap-x-10' : 'flex flex-col gap-8 lg:gap-10'}>
            {/* N2K LEFT COLUMN - FACETS*/}
            {!isRelatedPages && (<div className='hidden xl:flex flex-col pr-4 w-[306px] min-w-[306px]'>
              <SearchFacetMenu facets={facets} facetConfigMap={n2kFacetConfigMap} restrictedRegions={restrictedRegions}/>
            </div>)}
            

            {/* N2K RIGHT COLUMN - RESULTS */}
            <div className='flex flex-col flex-grow'>

              {/* Filter button - hide on xl */}
              {!isRelatedPages && (
                (showResults || showRefinements) && (
                    <Button
                      id="search-filter-button"
                      aria-label="Open search filters"
                      className={"xl:hidden mb-5 md:mb-8"}
                      size="sm"
                      width="md"
                      startDecorator={<Icon icon="sliders-simple" />}
                      endDecorator={
                        currentRefinements.items.length > 0 && (
                          <Badge>
                            {currentRefinements.items.reduce(
                              (total, item) => total + item.refinements.length,
                              0,
                            )}
                          </Badge>
                        )
                      }
                      onClick={(e: React.MouseEvent<any>) => {
                        setIsFilterMenuOpen(true);
                      }}
                    >
                      Filters
                    </Button>
                )    
              )}
          
              {/* RESULTS ITEMS */}
              {showResults && (
               <SearchResults
                  nbHits={nbHits}
                  results={results}
                  queryText={queryValue}
                  pathname={pathname}
                  promos={promos}
                />
              )}

              {showLoadState && <News2knowSearchSkeleton />}

              <div className='flex flex-col flex-grow gap-4'>
                {/* ERROR */}
                {showErrorState && (
                  <p>
                    We&apos;re sorry. An error has occurred on the page you were attempting to view.
                    The following information has been reported and we will address this as soon as
                    possible.
                  </p>
                )}

                {/* NO RESULTS */}
                {showNoResultsState && (
                  <>
                  <Typography variant='h3' className=' xl:pt-0'>
                    No results matched your search term.
                  </Typography>
                  <Typography variant="body">
                    Adjust the filters, try a different search term or select another search category.
                  </Typography>
                  <Typography variant="body">
                    Need help?&nbsp;
                    <Link
                      href="/contact-us"
                      className='text-tertiary hover:text-linkhover'
                      >
                      Contact us
                    </Link>
                  </Typography>
                  <Image
                    src="/assets/images/svgs/plant-illustration.svg"
                    width={200}
                    height={200}
                    alt="Sharp logo"
                    className='mt-4 xl:mt-8'
                  />                
                  </>
                )}
              </div>

            {/* PAGINATION */}
            {showPagination && (
              <div className='search-pagination pt-8 xl:pt-15'>
                <Pagination
                  total={Math.min(nbPages * results.hitsPerPage, nbHits)}
                  pageSize={results.hitsPerPage}
                  current={currentRefinement + 1}
                  onChange={onPageChange}
                />
              </div>
            )}
            </div>
          </div>

        </div>
      </Container>
    </div>
  )
}
export default Search